import React, { useCallback, useEffect, useMemo, useState } from "react"

import AgendaLegend from "../components/AgendaLegend"
import AgendaHeader from "../components/AgendaHeader"
import AgendaCalendar from "../components/AgendaCalendar"

import AppointmentDetailDrawer from "../drawers/AppointmentDetailDrawer"
import DragConflictDrawer from "../drawers/DragConflictDrawer"
import HeldReservationDrawer from "../drawers/HeldReservationDrawer"
import NewAppointmentDrawer from "../drawers/NewAppointmentDrawer"
import PatientWorkspaceDrawer from "../drawers/PatientWorkspaceDrawer"

import TreatmentPlanModal from "../modals/TreatmentPlanModal"
import { ui as uiSystem } from "../../../shared/styles/uiSystem"

export default function AgendaLayout({
  clinicId,
  clinicOptions,
  loadingClinics,
  date,
  setDate,
  userId,
  apiBase,
  patientQuery,
  ui,
  dataState,
  engine,
  actions,
  metrics,
  calendarView,
  setCalendarView,
  calendarRef,
  calendarHandlers,
  uiHelpers,
  setPatientQuery,
  isReadOnly,
  readOnlyReason,
}) {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440,
  )
  const [sidebarOpen, setSidebarOpen] = useState(true)
  
  const [selectedDoctorId, setSelectedDoctorId] = useState("ALL")
  const [selectedChairId, setSelectedChairId] = useState("ALL")

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const isMobile = viewportWidth < 768
  const isTablet = viewportWidth >= 768 && viewportWidth < 1100
  const isNarrowDesktop = viewportWidth < 1360
  const showSidebarAsColumn = isMobile || isTablet || isNarrowDesktop
  const isWeekView = calendarView === "timeGridWeek"

  useEffect(() => {
    if (showSidebarAsColumn) {
      setSidebarOpen(false)
    } else {
      setSidebarOpen(true)
    }
  }, [showSidebarAsColumn])

  const {
    events,
    patients,
    treatments,
    doctors,
    chairs,
    loadingPatients,
    dailyOpportunities,
  } = dataState

  const {
    specialtyLabel,
    statusLabel,
    planItemStatusLabel,
    formatMoney,
    formatDateTimeLocal,
    formatDateTime,
    surfaces,
    conditions,
    odontoStatuses,
  } = uiHelpers

  const { isProMode } = metrics

  const doctorFilterOptions = useMemo(() => {
    const map = new Map()

    ;(Array.isArray(doctors) ? doctors : []).forEach((doctor) => {
      const value =
        doctor?.id || doctor?.userId || doctor?.doctorId || doctor?.membershipId || ""
      const label =
        doctor?.name ||
        doctor?.fullName ||
        doctor?.doctorName ||
        doctor?.user?.name ||
        "Doctor"

      if (value && !map.has(value)) {
        map.set(value, { value, label })
      }
    })

    return Array.from(map.values())
  }, [doctors])

  const chairFilterOptions = useMemo(() => {
    const map = new Map()

    ;(Array.isArray(events) ? events : []).forEach((event) => {
      const chairId =
        event?.extendedProps?.chairId ||
        event?.chairId ||
        event?.resource?.id ||
        event?.resourceId ||
        ""

      const chairName =
        event?.extendedProps?.chairName ||
        event?.chairName ||
        event?.resource?.title ||
        event?.resource?.name ||
        ""

      if (chairId && !map.has(String(chairId))) {
        map.set(String(chairId), {
          value: String(chairId),
          label: chairName || `Silla ${String(chairId).slice(0, 4)}`,
        })
      }
    })

    return Array.from(map.values())
  }, [events])

  useEffect(() => {
    setSelectedDoctorId("ALL")
    setSelectedChairId("ALL")
  }, [clinicId])

  useEffect(() => {
    if (selectedDoctorId === "ALL") return
    const exists = doctorFilterOptions.some((item) => item.value === selectedDoctorId)
    if (!exists) setSelectedDoctorId("ALL")
  }, [doctorFilterOptions, selectedDoctorId])

  useEffect(() => {
    if (selectedChairId === "ALL") return
    const exists = chairFilterOptions.some((item) => item.value === selectedChairId)
    if (!exists) setSelectedChairId("ALL")
  }, [chairFilterOptions, selectedChairId])

  const selectedDoctorLabel = useMemo(() => {
    if (selectedDoctorId === "ALL") return "Todos los doctores"
    return (
      doctorFilterOptions.find((item) => item.value === selectedDoctorId)?.label || "Doctor"
    )
  }, [doctorFilterOptions, selectedDoctorId])

  const selectedChairLabel = useMemo(() => {
    if (selectedChairId === "ALL") return "Todas las sillas"
    return chairFilterOptions.find((item) => item.value === selectedChairId)?.label || "Silla"
  }, [chairFilterOptions, selectedChairId])

  const getEntityIdCandidates = useCallback((item) => {
    if (!item) return []

    return [
      item?.doctorId,
      item?.chairId,
      item?.assignedDoctorId,
      item?.selectedDoctorId,
      item?.resourceId,
      item?.extendedProps?.doctorId,
      item?.extendedProps?.chairId,
      item?.appointment?.doctorId,
      item?.appointment?.chairId,
      item?.event?.doctorId,
      item?.event?.chairId,
      item?.event?.extendedProps?.doctorId,
      item?.event?.extendedProps?.chairId,
      item?.meta?.doctorId,
      item?.meta?.chairId,
      item?.context?.doctorId,
      item?.context?.chairId,
      item?.data?.doctorId,
      item?.data?.chairId,
    ]
      .filter(Boolean)
      .map(String)
  }, [])

  const matchesFilters = useCallback(
    (item) => {
      const ids = getEntityIdCandidates(item)

      const doctorOk =
        selectedDoctorId === "ALL" ||
        ids.length === 0 ||
        ids.includes(String(selectedDoctorId))

      const chairOk =
        selectedChairId === "ALL" ||
        ids.length === 0 ||
        ids.includes(String(selectedChairId))

      return doctorOk && chairOk
    },
    [getEntityIdCandidates, selectedDoctorId, selectedChairId],
  )

  const filteredEvents = useMemo(() => {
    const safeEvents = Array.isArray(events) ? events : []

    return safeEvents.filter((event) => {
      const doctorOk =
        selectedDoctorId === "ALL" ||
        String(event?.extendedProps?.doctorId || "") === String(selectedDoctorId)

      const chairOk =
        selectedChairId === "ALL" ||
        String(event?.extendedProps?.chairId || "") === String(selectedChairId)

      return doctorOk && chairOk
    })
  }, [events, selectedDoctorId, selectedChairId])

  const filteredAppointmentEvents = useMemo(
    () => filteredEvents.filter((event) => !isHeldEvent(event)),
    [filteredEvents],
  )

  const filteredHeldEvents = useMemo(
    () => filteredEvents.filter((event) => isHeldEvent(event)),
    [filteredEvents],
  )

  const filteredRecallEvents = useMemo(
    () => filteredEvents.filter((event) => isRecallEvent(event)),
    [filteredEvents],
  )

  const filteredPlanLinkedEvents = useMemo(
    () => filteredEvents.filter((event) => Boolean(event?.extendedProps?.isPlanLinked)),
    [filteredEvents],
  )

  const opportunityRows = useMemo(
    () => (Array.isArray(dailyOpportunities?.rows) ? dailyOpportunities.rows : []),
    [dailyOpportunities],
  )

  const filteredOpportunityRows = useMemo(
    () => opportunityRows.filter((row) => matchesFilters(row)),
    [opportunityRows, matchesFilters],
  )

  const filteredActionItems = useMemo(
    () =>
      (Array.isArray(metrics.actionItems) ? metrics.actionItems : []).filter((item) =>
        matchesFilters(item),
      ),
    [metrics.actionItems, matchesFilters],
  )

  const filteredDelayRiskItems = useMemo(
    () =>
      (Array.isArray(metrics.delayRiskItems) ? metrics.delayRiskItems : []).filter((item) =>
        matchesFilters(item),
      ),
    [metrics.delayRiskItems, matchesFilters],
  )

  const filteredCapacityItems = useMemo(
    () =>
      (Array.isArray(metrics.capacityItems) ? metrics.capacityItems : []).filter((item) =>
        matchesFilters(item),
      ),
    [metrics.capacityItems, matchesFilters],
  )

  const filteredAllOpportunityItems = useMemo(
    () =>
      (Array.isArray(metrics.allOpportunityItems) ? metrics.allOpportunityItems : []).filter(
        (item) => matchesFilters(item),
      ),
    [metrics.allOpportunityItems, matchesFilters],
  )

  const dailyHeroData = {
    totalAppointments: filteredAppointmentEvents.length,
    recallAppointments: filteredRecallEvents.length,
    opportunities: filteredOpportunityRows.length,
    planLinkedAppointments: filteredPlanLinkedEvents.length,
  }

  const weeklySummary = useMemo(() => {
    const appointmentEvents = filteredAppointmentEvents
    const heldEvents = filteredHeldEvents
    const recallEvents = filteredRecallEvents
    const planLinkedEvents = filteredPlanLinkedEvents

    const uniquePatients = new Set(
      appointmentEvents
        .map((event) => event?.extendedProps?.patientId || event?.title || "")
        .filter(Boolean),
    ).size

    const totalHours = appointmentEvents.reduce((acc, event) => {
      const start = event?.start ? new Date(event.start) : null
      const end = event?.end ? new Date(event.end) : null
      if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return acc
      }

      return acc + Math.max(0, (end.getTime() - start.getTime()) / 3600000)
    }, 0)

    const dayMap = new Map()
    appointmentEvents.forEach((event) => {
      const key = getLocalDateKey(event?.start)
      if (!key) return
      dayMap.set(key, (dayMap.get(key) || 0) + 1)
    })

    let busiestDayKey = ""
    let busiestDayCount = 0
    for (const [key, count] of dayMap.entries()) {
      if (count > busiestDayCount) {
        busiestDayCount = count
        busiestDayKey = key
      }
    }

    const doctorMap = new Map()
    appointmentEvents.forEach((event) => {
      const doctorId = event?.extendedProps?.doctorId || ""
      const doctorName = event?.extendedProps?.doctorName || "Doctor"
      const key = doctorId || doctorName
      if (!key) return

      doctorMap.set(key, {
        doctorName,
        count: (doctorMap.get(key)?.count || 0) + 1,
      })
    })

    let busiestDoctor = null
    for (const item of doctorMap.values()) {
      if (!busiestDoctor || item.count > busiestDoctor.count) {
        busiestDoctor = item
      }
    }

    return {
      totalAppointments: appointmentEvents.length,
      heldAppointments: heldEvents.length,
      recallAppointments: recallEvents.length,
      planLinkedAppointments: planLinkedEvents.length,
      uniquePatients,
      totalHours,
      busiestDayLabel: busiestDayKey ? formatShortDayLabel(busiestDayKey) : "—",
      busiestDayCount,
      doctorsCount: doctorFilterOptions.length,
      busiestDoctorName: busiestDoctor?.doctorName || "—",
      busiestDoctorCount: busiestDoctor?.count || 0,
    }
  }, [
    filteredAppointmentEvents,
    filteredHeldEvents,
    filteredRecallEvents,
    filteredPlanLinkedEvents,
    doctorFilterOptions.length,
  ])

  const weeklyHeroPills = useMemo(
    () => [
      { label: "Citas semana", value: weeklySummary.totalAppointments },
      { label: "Pacientes", value: weeklySummary.uniquePatients },
      { label: "Reservas", value: weeklySummary.heldAppointments },
      { label: "Horas", value: formatHours(weeklySummary.totalHours) },
    ],
    [weeklySummary],
  )

  const showInsightsPanel = true


    const dailySideSummary = useMemo(() => {
    const selectedDateKey = String(date || "").slice(0, 10)
    const todayKey = getLocalDateKey(new Date())

    const eventsForSelectedDay = filteredAppointmentEvents
      .map((event) => {
        const start = event?.start ? new Date(event.start) : null

        return {
          event,
          start,
          time: start && !Number.isNaN(start.getTime()) ? start.getTime() : 0,
        }
      })
      .filter((item) => {
        if (!item.start || Number.isNaN(item.start.getTime())) return false
        return getLocalDateKey(item.start) === selectedDateKey
      })
      .sort((a, b) => a.time - b.time)

    const baseTime =
      selectedDateKey === todayKey
        ? Date.now()
        : new Date(`${selectedDateKey || todayKey}T00:00:00`).getTime()

    const nextItem =
      eventsForSelectedDay.find((item) => item.time >= baseTime) ||
      eventsForSelectedDay[0] ||
      null

    const totalActionItems =
      filteredActionItems.length + filteredDelayRiskItems.length

    return {
      appointments: filteredAppointmentEvents.length,
      reservations: filteredHeldEvents.length,
      recall: filteredRecallEvents.length,
      plan: filteredPlanLinkedEvents.length,
      capacityAlerts: filteredCapacityItems.length,
      actionItems: totalActionItems,
      opportunities: filteredAllOpportunityItems.length,
      nextAppointmentTitle: nextItem?.event?.title || "",
      nextAppointmentTime: nextItem?.start ? formatShortTimeLabel(nextItem.start) : "",
    }
  }, [
    date,
    filteredAppointmentEvents,
    filteredHeldEvents,
    filteredRecallEvents,
    filteredPlanLinkedEvents,
    filteredCapacityItems,
    filteredActionItems,
    filteredDelayRiskItems,
    filteredAllOpportunityItems,
  ])

  const showDoctorFilter = doctorFilterOptions.length > 1
  const showChairFilter = chairFilterOptions.length > 1

  return (
    <div style={uiSystem.page}>
      <AgendaHeader
        clinics={clinicOptions}
        clinicId={clinicId}
        date={date}
        setDate={setDate}
        loadingClinics={loadingClinics}
        compact={isMobile}
        onNewAppointment={() => {
          if (isReadOnly) {
            alert(readOnlyReason || "Modo solo lectura activo")
            return
          }

          if (!clinicId || clinicId === "ALL") {
            alert("Modo multi-clínica activo. Selecciona una clínica para crear citas.")
            return
          }

          actions.openEmptyNewAppointment()
        }}
      />

      {isReadOnly && clinicId !== "ALL" && (
        <div
          style={{
            ...uiSystem.dangerCard,
            marginBottom: 12,
            fontWeight: 700,
          }}
        >
          {readOnlyReason || "La clínica está en modo solo lectura."}
        </div>
      )}

      {clinicId === "ALL" && (
        <div
          style={{
            ...uiSystem.warningCard,
            marginBottom: 12,
            fontWeight: 700,
          }}
        >
          Modo multi-clínica activo · Vista consolidada · Solo lectura
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            showInsightsPanel && sidebarOpen && !showSidebarAsColumn
              ? "minmax(0, 1fr) 320px"
              : "minmax(0, 1fr)",
          gap: 14,
          alignItems: "start",
        }}
      >
        <div
          style={{
            display: "grid",
            gap: isMobile ? 8 : 12,
          }}
        >
          <div
            style={{
              ...uiSystem.card,
              padding: isMobile ? 8 : 12,
            }}
          >
            <div
              style={{
                display: "grid",
                gap: isMobile ? 7 : 10,
                marginBottom: isMobile ? 7 : 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: isMobile ? 6 : 8,
                  flexWrap: isMobile ? "nowrap" : "wrap",
                  alignItems: "center",
                  overflowX: isMobile ? "auto" : "visible",
                  paddingBottom: isMobile ? 2 : 0,
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {isWeekView ? (
                  weeklyHeroPills.map((item) => (
                    <MiniPill
                      key={item.label}
                      label={item.label}
                      value={item.value}
                      compact={isMobile}
                    />
                  ))
                ) : (
                  <>
                    <MiniPill label="Citas" value={dailyHeroData.totalAppointments} compact={isMobile} />
                    <MiniPill label="Recall" value={dailyHeroData.recallAppointments} compact={isMobile} />
                    <MiniPill label="Oport." value={dailyHeroData.opportunities} compact={isMobile} />
                    <MiniPill label="Plan" value={dailyHeroData.planLinkedAppointments} compact={isMobile} />
                  </>
                )}

                {showInsightsPanel && !showSidebarAsColumn ? (
                  <button
                    type="button"
                    onClick={() => setSidebarOpen((prev) => !prev)}
                    style={{
                      ...(sidebarOpen ? uiSystem.secondaryButton : uiSystem.primaryButton),
                      marginLeft: isMobile ? 0 : "auto",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {sidebarOpen ? "Ocultar panel lateral" : "Mostrar panel lateral"}
                  </button>
                ) : null}
              </div>

              {(showDoctorFilter || showChairFilter) && (
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: isMobile ? "stretch" : "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                      alignItems: "center",
                      flex: 1,
                    }}
                  >
                    {showDoctorFilter ? (
                      <FilterSelect
                        value={selectedDoctorId}
                        onChange={setSelectedDoctorId}
                        options={[
                          { value: "ALL", label: "Todos los doctores" },
                          ...doctorFilterOptions,
                        ]}
                        minWidth={220}
                        fullWidth={isMobile}
                      />
                    ) : null}

                    {showChairFilter ? (
                      <FilterSelect
                        value={selectedChairId}
                        onChange={setSelectedChairId}
                        options={[
                          { value: "ALL", label: "Todas las sillas" },
                          ...chairFilterOptions,
                        ]}
                        minWidth={220}
                        fullWidth={isMobile}
                      />
                    ) : null}
                  </div>

                </div>
              )}
            </div>

            {isProMode ? (
              <div style={{ marginBottom: isMobile ? 6 : 10 }}>
                <AgendaLegend compact={isMobile} />
              </div>
            ) : null}

            <AgendaCalendar
              key={`${clinicId || "no-clinic"}`}
              calendarRef={calendarRef}
              clinicId={clinicId}
              date={date}
              events={filteredEvents}
              calendarView={calendarView}
              setCalendarView={setCalendarView}
              handleDatesSet={calendarHandlers.handleDatesSet}
              handleEventDrop={actions.handleEventDrop}
              handleEventResize={actions.handleEventResize}
              handleDateClick={actions.handleDateClick}
              handleEventClick={calendarHandlers.handleCalendarEventClick}
              compact={isMobile}
              onBlockedSlotClick={({ reason }) => {
                const goToSettings = window.confirm(
                  `${reason || "Este horario no está disponible para agendar."}\n\n¿Quieres abrir la configuración de horarios?`
                )

                if (goToSettings) {
                  window.location.href = "/settings/schedule-availability"
                }
              }}
            />
          </div>

          {showInsightsPanel && showSidebarAsColumn && (
            <div style={{ display: "grid", gap: 12 }}>
              {isWeekView ? (
                <>
                  <AccordionBlock title="📅  Resumen semanal" defaultOpen>
                    <WeeklySummaryCard
                      summary={weeklySummary}
                      selectedDoctorLabel={selectedDoctorLabel}
                      selectedChairLabel={selectedChairLabel}
                      doctorFilterActive={selectedDoctorId !== "ALL"}
                      chairFilterActive={selectedChairId !== "ALL"}
                    />
                  </AccordionBlock>


                </>
              ) : (
                <AccordionBlock title="Resumen operativo" defaultOpen={false}>
                  <AgendaSideCompactCard
                    summary={dailySideSummary}
                    actions={actions}
                    isReadOnly={isReadOnly}
                    readOnlyReason={readOnlyReason}
                    uiSystem={uiSystem}
                    compact
                  />
                </AccordionBlock>
              )}
            </div>
          )}
        </div>

        {showInsightsPanel && sidebarOpen && !showSidebarAsColumn && (
          <div
            style={{
              display: "grid",
              gap: 12,
              alignContent: "start",
              position: "sticky",
              top: 96,
            }}
          >
            {isWeekView ? (
              <>
                <WeeklySummaryCard
                  summary={weeklySummary}
                  selectedDoctorLabel={selectedDoctorLabel}
                  selectedChairLabel={selectedChairLabel}
                  doctorFilterActive={selectedDoctorId !== "ALL"}
                  chairFilterActive={selectedChairId !== "ALL"}
                />
              </>
            ) : (
              <AgendaSideCompactCard
                summary={dailySideSummary}
                actions={actions}
                isReadOnly={isReadOnly}
                readOnlyReason={readOnlyReason}
                uiSystem={uiSystem}
              />
            )}
          </div>
        )}
      </div>

      <NewAppointmentDrawer
        open={ui.showNewModal}
        clinicId={clinicId}
        loadingPatients={loadingPatients}
        patientQuery={patientQuery}
        setPatientQuery={setPatientQuery}
        newAppt={ui.newAppt}
        setNewAppt={ui.setNewAppt}
        patients={patients}
        quickPatient={ui.quickPatient}
        setQuickPatient={ui.setQuickPatient}
        creatingPatient={ui.creatingPatient}
        createQuickPatient={actions.createQuickPatient}
        treatments={treatments}
        doctors={doctors}
        chairs={chairs}
        onTreatmentChange={actions.onTreatmentChange}
        specialtyLabel={specialtyLabel}
        suggestDoctor={engine.suggestDoctor}
        suggestingDoctor={engine.suggestingDoctor}
        loadAlternatives={engine.loadAlternatives}
        loadingAlternatives={engine.loadingAlternatives}
        holdAppointment={actions.holdAppointment}
        createAppointment={actions.createAppointment}
        saving={ui.saving}
        doctorSuggestion={engine.doctorSuggestion}
        alternatives={engine.alternatives}
        loadingSuggestion={engine.loadingSuggestion}
        formatDateTimeLocal={formatDateTimeLocal}
        isReadOnly={isReadOnly}
        readOnlyReason={readOnlyReason}
        onClose={() => {
          ui.setShowNewModal(false)
          actions.resetNewAppointmentState()
        }}
      />

      <AppointmentDetailDrawer
        appointment={ui.selectedAppointment}
        onClose={() => {
          ui.setSelectedAppointment(null)
          ui.setTreatmentPlans([])
        }}
        patientSnapshot={ui.patientSnapshot}
        financialSummary={ui.financialSummary}
        loadingFinancialSummary={ui.loadingFinancialSummary}
        checkingIn={ui.checkingIn}
        registeringTreatment={ui.registeringTreatment}
        registeringPayment={ui.registeringPayment}
        creatingReceivable={ui.creatingReceivable}
        updatingAppointment={ui.saving}
        treatments={treatments}
        doctors={doctors}
        chairs={chairs}
        selectedAppointmentTreatmentId={ui.selectedAppointmentTreatmentId}
        setSelectedAppointmentTreatmentId={ui.setSelectedAppointmentTreatmentId}
        treatmentOdontogramForm={ui.treatmentOdontogramForm}
        setTreatmentOdontogramForm={ui.setTreatmentOdontogramForm}
        surfaces={surfaces}
        conditions={conditions}
        odontoStatuses={odontoStatuses}
        paymentForm={ui.paymentForm}
        setPaymentForm={ui.setPaymentForm}
        receivableForm={ui.receivableForm}
        setReceivableForm={ui.setReceivableForm}
        formatDateTime={formatDateTime}
        formatMoney={formatMoney}
        statusLabel={statusLabel}
        isReadOnly={isReadOnly}
        readOnlyReason={readOnlyReason}
        onOpenPatientWorkspace={actions.openPatientWorkspace}
        onOpenTreatmentPlanModal={
          isReadOnly
            ? () => alert(readOnlyReason || "Modo solo lectura activo")
            : () => ui.setShowTreatmentPlanModal(true)
        }
        onCheckIn={actions.checkInSelectedAppointment}
        onRegisterTreatment={actions.registerTreatmentForSelectedAppointment}
        onDeleteTreatment={actions.deleteTreatmentFromSelectedAppointment}
        onRegisterPayment={actions.registerPaymentForSelectedAppointment}
        onCancelPayment={actions.cancelPaymentForSelectedAppointment}
        onCreateReceivable={actions.createReceivableForSelectedAppointment}
        onUpdateAppointmentColor={actions.updateSelectedAppointmentColor}
        onUpdateAppointment={actions.updateSelectedAppointment}
        onCancelAppointment={actions.cancelSelectedAppointment}
        onDeleteAppointment={actions.deleteSelectedAppointment}
        onOpenPatientPage={(tab) =>
          actions.openPatientFromAgenda(
            ui.selectedAppointment?.patientId,
            ui.selectedAppointment?.clinicId,
            tab
          )
        }
      />

      <PatientWorkspaceDrawer
        open={ui.patientWorkspaceOpen}
        tab={ui.patientWorkspaceTab}
        onClose={(reason) => {
          ui.setPatientWorkspaceOpen(false)

          if (reason === "backdrop") {
            ui.setSelectedAppointment(null)
            ui.setTreatmentPlans([])
          }
        }}
        onOpenTab={actions.openPatientWorkspace}
        loadingPatientSnapshot={ui.loadingPatientSnapshot}
        loadingPatientOdontogram={ui.loadingPatientOdontogram}
        loadingTreatmentPlans={ui.loadingTreatmentPlans}
        patientSnapshot={ui.patientSnapshot}
        patientOdontogram={ui.patientOdontogram}
        treatmentPlans={ui.treatmentPlans}
        selectedAppointment={ui.selectedAppointment}
        formatDateTime={formatDateTime}
        formatMoney={formatMoney}
        planItemStatusLabel={planItemStatusLabel}
        creatingOdontogramEntry={ui.creatingOdontogramEntry}
        odontogramForm={ui.odontogramForm}
        setOdontogramForm={ui.setOdontogramForm}
        surfaces={surfaces}
        conditions={conditions}
        odontoStatuses={odontoStatuses}
        onCreateOdontogramEntry={actions.createOdontogramEntry}
        onScheduleFromPlanItem={actions.scheduleFromPlanItem}
        patientAlerts={ui.patientAlerts}
        loadingPatientAlerts={ui.loadingPatientAlerts}
        onAlertAction={actions.handlePatientAlertAction}
      />

      <HeldReservationDrawer
        reservation={ui.selectedHeldEvent}
        onConfirm={actions.confirmHeld}
        onCancel={actions.cancelHeld}
        onClose={() => ui.setSelectedHeldEvent(null)}
      />

      <DragConflictDrawer
        conflict={ui.dragConflictInfo}
        alternatives={ui.dragAlternatives}
        loading={ui.loadingDragAlternatives}
        specialtyLabel={specialtyLabel}
        onApply={actions.applyDragAlternative}
        onClose={() => {
          ui.setDragConflictInfo(null)
          ui.setDragAlternatives([])
        }}
      />

      <TreatmentPlanModal
        open={ui.showTreatmentPlanModal}
        onClose={() => ui.setShowTreatmentPlanModal(false)}
        onCreated={async () => {
          if (ui.selectedAppointment?.patientId) {
            await actions.loadPatientTreatmentPlans(ui.selectedAppointment.patientId)
            await actions.loadPatientSnapshot(ui.selectedAppointment.patientId)
          }
        }}
        clinicId={clinicId}
        patient={ui.patientSnapshot?.patient || null}
        userId={userId}
        treatments={treatments}
        doctors={doctors}
        chairs={chairs}
        apiBase={apiBase}
      />
    </div>
  )
}

function FilterSelect({
  value,
  onChange,
  options,
  minWidth = 220,
  fullWidth = false,
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        minHeight: fullWidth ? 36 : 40,
        width: fullWidth ? "100%" : "auto",
        minWidth: fullWidth ? "100%" : minWidth,
        maxWidth: fullWidth ? "100%" : 280,
        flex: fullWidth ? "1 1 100%" : "0 1 260px",
        borderRadius: 12,
        border: "1px solid #CBD5E1",
        background: "#FFFFFF",
        padding: "0 12px",
        fontSize: fullWidth ? 12.5 : 13,
        fontWeight: 700,
        color: "#0F172A",
        outline: "none",
      }}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

function isHeldEvent(event) {
  return (
    String(event?.id || "").startsWith("held-") ||
    Boolean(event?.extendedProps?.isHeld) ||
    event?.extendedProps?.status === "HELD"
  )
}

function isRecallEvent(event) {
  const source = String(event?.extendedProps?.source || "")
  const reason = String(event?.extendedProps?.reason || "")
  return source.toUpperCase().includes("RECALL") || reason.toUpperCase().includes("RECALL")
}

function getLocalDateKey(value) {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""

  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, "0")
  const dd = String(date.getDate()).padStart(2, "0")

  return `${yyyy}-${mm}-${dd}`
}

function formatShortDayLabel(dateKey) {
  if (!dateKey) return "—"

  return new Intl.DateTimeFormat("es-MX", {
    weekday: "short",
    day: "numeric",
  }).format(new Date(`${dateKey}T12:00:00`))
}

function formatHours(value) {
  const rounded = Math.round(Number(value || 0) * 10) / 10
  return `${rounded} h`
}

function MiniPill({ label, value, compact = false }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? 5 : 6,
        minHeight: compact ? 27 : 32,
        padding: compact ? "5px 8px" : "7px 11px",
        borderRadius: 999,
        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: "1px solid #e2e8f0",
        color: "#475569",
        fontWeight: 800,
        fontSize: compact ? 11 : 12,
        whiteSpace: "nowrap",
        flex: "0 0 auto",
        boxShadow: compact ? "none" : "0 8px 18px rgba(15, 23, 42, 0.04)",
      }}
    >
      <span>{label}</span>
      <strong
        style={{
          color: "#0f172a",
          fontWeight: 950,
        }}
      >
        {value}
      </strong>
    </div>
  )
}

function AgendaSideCompactCard({
  summary,
  actions,
  isReadOnly,
  readOnlyReason,
  uiSystem,
  compact = false,
}) {
  const hasNextAppointment =
    summary?.nextAppointmentTitle && summary?.nextAppointmentTime

  const focusText = (() => {
    if ((summary?.appointments || 0) <= 0) {
      return "Día libre. Buen momento para recall, reactivación o confirmar reservas."
    }

    if ((summary?.reservations || 0) > 0) {
      return "Hay reservas pendientes. Conviene confirmar antes de llenar nuevos espacios."
    }

  if ((summary?.actionItems || 0) > 0) {
    return `Hay ${summary.actionItems} acción${summary.actionItems === 1 ? "" : "es"} sugerida${summary.actionItems === 1 ? "" : "s"} para revisar con más detalle en Dashboard.`
  }

    return "Operación estable. Mantén seguimiento a huecos y próximas citas."
  })()

  const handleNewAppointment = () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return
    }

    actions.openEmptyNewAppointment?.()
  }

  return (
    <div
      style={{
        ...uiSystem.sectionCard,
        padding: compact ? 12 : 14,
        display: "grid",
        gap: 12,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 950,
            color: "#0f172a",
            letterSpacing: "-0.03em",
          }}
        >
          Resumen operativo
        </div>

        <div
          style={{
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
            marginTop: 3,
            lineHeight: 1.35,
          }}
        >
          Lectura rápida para operar la agenda.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
        }}
      >
        <SideMetric label="Citas" value={summary?.appointments || 0} tone="blue" />
        <SideMetric label="Reservas" value={summary?.reservations || 0} tone="orange" />
        <SideMetric label="Recall" value={summary?.recall || 0} tone="teal" />
        <SideMetric label="Plan" value={summary?.plan || 0} tone="green" />
      </div>

      <div
        style={{
          padding: 12,
          borderRadius: 16,
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          display: "grid",
          gap: 7,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Próxima cita
        </div>

        {hasNextAppointment ? (
          <>
            <div
              style={{
                fontSize: 19,
                fontWeight: 950,
                color: "#0f172a",
                letterSpacing: "-0.04em",
                lineHeight: 1.05,
              }}
            >
              {summary.nextAppointmentTime}
            </div>

            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: "#334155",
                lineHeight: 1.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={summary.nextAppointmentTitle}
            >
              {summary.nextAppointmentTitle}
            </div>
          </>
        ) : (
          <div
            style={{
              fontSize: compact ? 12.5 : 13,
              fontWeight: 800,
              color: "#64748b",
              lineHeight: 1.35,
            }}
          >
            Sin próxima cita para este corte.
          </div>
        )}
      </div>

      <div
        style={{
          padding: 12,
          borderRadius: 16,
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#1e3a8a",
          fontSize: 12,
          fontWeight: 800,
          lineHeight: 1.35,
        }}
      >
        {focusText}
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        <button
          type="button"
          onClick={handleNewAppointment}
          style={{
            ...uiSystem.primaryButton,
            width: "100%",
            justifyContent: "center",
          }}
        >
          + Nueva cita
        </button>
        <button
          type="button"
          onClick={() => {
            if (typeof window !== "undefined") {
              window.localStorage.setItem("odontoflow_dashboard_focus", "agenda-actions")
              window.localStorage.setItem("odontoflow_dashboard_source", "agenda")
              window.location.href = "/dashboard?focus=agenda-actions&source=agenda"
            }
          }}
          style={{
            ...uiSystem.secondaryButton,
            width: "100%",
            justifyContent: "center",
          }}
        >
          Ver acciones en Dashboard
        </button>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
          }}
        >
          <SmallSideCounter label="Alertas" value={summary?.actionItems || 0} />
          <SmallSideCounter label="Oportunidades" value={summary?.opportunities || 0} />
        </div>
      </div>
    </div>
  )
}

function SideMetric({ label, value, tone = "blue" }) {
  const tones = {
    blue: {
      bg: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8",
    },
    orange: {
      bg: "#fff7ed",
      border: "#fed7aa",
      color: "#c2410c",
    },
    teal: {
      bg: "#ecfeff",
      border: "#a5f3fc",
      color: "#0f766e",
    },
    green: {
      bg: "#f0fdf4",
      border: "#bbf7d0",
      color: "#166534",
    },
  }

  const selected = tones[tone] || tones.blue

  return (
    <div
      style={{
        minHeight: 74,
        padding: 11,
        borderRadius: 16,
        background: selected.bg,
        border: `1px solid ${selected.border}`,
        display: "grid",
        alignContent: "center",
        gap: 4,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 900,
          color: "#64748b",
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 24,
          fontWeight: 950,
          color: selected.color,
          letterSpacing: "-0.05em",
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  )
}

function SmallSideCounter({ label, value }) {
  return (
    <div
      style={{
        padding: "9px 10px",
        borderRadius: 14,
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: "#64748b",
        }}
      >
        {label}
      </span>

      <strong
        style={{
          fontSize: 13,
          color: "#0f172a",
          fontWeight: 950,
        }}
      >
        {value}
      </strong>
    </div>
  )
}

function formatShortTimeLabel(value) {
  const date = value instanceof Date ? value : new Date(value)

  if (Number.isNaN(date.getTime())) return "—"

  return new Intl.DateTimeFormat("es-MX", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date)
}

function WeeklySummaryCard({
  summary,
  selectedDoctorLabel,
  selectedChairLabel,
  doctorFilterActive,
  chairFilterActive,
}) {
  const doctorsLabel =
    summary.doctorsCount > 1 && !doctorFilterActive ? "Dr. más cargado" : "Horas ocupadas"

  const doctorsValue =
    summary.doctorsCount > 1 && !doctorFilterActive
      ? summary.busiestDoctorName === "—"
        ? "—"
        : `${summary.busiestDoctorName} · ${summary.busiestDoctorCount}`
      : formatHours(summary.totalHours)

  return (
    <div
      style={{
        ...uiSystem.sectionCard,
        padding: 12,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 900, color: "#0f172a", marginBottom: 8 }}>
        Resumen de la semana
      </div>

      {doctorFilterActive || chairFilterActive ? (
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          {doctorFilterActive ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                background: "#EFF6FF",
                border: "1px solid #BFDBFE",
                color: "#1D4ED8",
                fontSize: 11,
                fontWeight: 900,
              }}
            >
              {selectedDoctorLabel}
            </span>
          ) : null}

          {chairFilterActive ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                background: "#F8FAFC",
                border: "1px solid #CBD5E1",
                color: "#334155",
                fontSize: 11,
                fontWeight: 900,
              }}
            >
              {selectedChairLabel}
            </span>
          ) : null}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <CompactStatCard
          label="Citas"
          value={summary.totalAppointments}
          color="#1d4ed8"
          bg="#eff6ff"
          border="#bfdbfe"
        />
        <CompactStatCard
          label="Pacientes"
          value={summary.uniquePatients}
          color="#0f766e"
          bg="#ecfeff"
          border="#a5f3fc"
        />
        <CompactStatCard
          label="Reservas"
          value={summary.heldAppointments}
          color="#c2410c"
          bg="#fff7ed"
          border="#fed7aa"
        />
        <CompactStatCard
          label="Plan"
          value={summary.planLinkedAppointments}
          color="#166534"
          bg="#f0fdf4"
          border="#bbf7d0"
        />
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        <WeeklyInfoRow
          label="Día más cargado"
          value={
            summary.busiestDayLabel === "—"
              ? "—"
              : `${summary.busiestDayLabel} · ${summary.busiestDayCount} cita(s)`
          }
        />
        <WeeklyInfoRow label={doctorsLabel} value={doctorsValue} />
        <WeeklyInfoRow label="Recall" value={summary.recallAppointments} />
      </div>
    </div>
  )
}

function WeeklyInfoRow({ label, value }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "120px minmax(0, 1fr)",
        gap: 10,
        alignItems: "center",
      }}
    >
      <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>{label}</div>
      <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 900 }}>{value}</div>
    </div>
  )
}



function CompactStatCard({ label, value, color, bg, border }) {
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 14,
        background: bg,
        border: `1px solid ${border}`,
      }}
    >
      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 900, color }}>{value}</div>
    </div>
  )
}

function AccordionBlock({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div style={{ ...uiSystem.card, padding: 10 }}>
      <div
        onClick={() => setOpen((prev) => !prev)}
        style={{
          cursor: "pointer",
          fontWeight: 900,
          fontSize: 14,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {title}
        <span>{open ? "−" : "+"}</span>
      </div>

      {open && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  )
}
